// One release (Console D's #/releases/:v, APS-307): its numbers against the release before, the
// daily crash-free line since it shipped against the alert, what changed (new, regressed, still
// open, resolved), performance against the release before and its screens, the images that still
// need symbols, adoption by day, its devices and OS versions, and its facts.
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import {
  breakdownQuery,
  framesQuery,
  issuesQuery,
  missingSymbolsQuery,
  releaseDaysQuery,
  screensQuery,
  vitalsQuery,
} from '../api/queries'
import type { App, IssueSummary, MissingSymbol, ReleaseHealth } from '../api/types'
import { AffectedIssues } from '../components/AffectedIssues'
import {
  Bars,
  CodeTabs,
  EmptyNote,
  Kind,
  KV,
  PageHead,
  Section,
  ShareBars,
  Status,
  type StatusKey,
  Strip,
  type StripItem,
} from '../components/d'
import { copyText, useToast } from '../components/d/Toast'
import { Icon } from '../components/icons'
import { LineChart } from '../components/LineChart'
import { deviceMeta, LatencyText } from '../components/Perf'
import { ChangeCell, CompareTable, useComparison } from '../components/ReleaseCompare'
import { appLabel } from '../components/Shell'
import { ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import { compact, duration, percentSpaced } from '../lib/format'
import { issueTitle } from '../lib/issues'
import { ttidAt } from '../lib/latency'
import { adoptionByDay, cliCompare, SIGNALS, versionBuild } from '../lib/releases'
import { SYMBOLS_DOCS } from './Issue'
import { useProjectParams } from './params'
import {
  releaseWindow,
  useCrashFreeLimit,
  useReleaseList,
  useSampled,
  WINDOW_DAYS,
} from './Releases'
import {
  fileSource,
  KIND_KEY,
  NEED_KEY,
  neededFile,
  SOURCE_KEY,
  shortDebugId,
  shownKind,
  uploadTabs,
} from './SymbolsSettings'
import { deviceParams, type ScopeSearch } from './search'

/** Rows a "What changed" group shows before linking to the rest. */
const GROUP_ROWS = 5
/** Screens the minor table lists, most loaded first. */
const SCREENS = 8

export function ReleasePage() {
  const i18n = useI18n()
  // A release's page is by device already: a carried device filter is said not to apply (APS-228).
  const notApplied = deviceMeta(
    i18n,
    undefined,
    deviceParams(useSearch({ strict: false }) as ScopeSearch),
    'na',
  )
  const { t, date } = i18n
  const { orgId, projectId } = useProjectParams()
  const { release: name } = useParams({ strict: false }) as { release: string }
  const { all, appId, app, list, now, share } = useReleaseList()
  const sampled = useSampled()
  const android = app?.platform === 'android'
  const index = list.findIndex((r) => r.release === name)
  const r = list[index]
  const prev = index >= 0 ? list[index + 1] : undefined
  const newer = index > 0 ? list[index - 1] : undefined
  const cmp = useComparison({ projectId, appId, android, base: prev, candidate: r })
  const { toast, show } = useToast()
  const params = { orgId, projectId }
  const scope = appId ? { app: appId } : {}
  const crumb = (
    <>
      <Link to="/o/$orgId/p/$projectId/releases" params={params} search={scope}>
        {t('nav.releases')}
      </Link>
      {app ? ` / ${appLabel(app)}` : ''}
    </>
  )

  if (all.isPending || (all.isFetching && list.length === 0))
    return (
      <main id="main" className="pg rs-pg">
        <Loading />
      </main>
    )
  if (all.isError)
    return (
      <main id="main" className="pg rs-pg">
        <PageHead crumb={crumb} title={name} variant="mono" />
        <ErrorState error={all.error} retry={() => all.refetch()} />
      </main>
    )
  if (!r)
    return (
      <main id="main" className="pg rs-pg">
        <PageHead crumb={crumb} title={name} variant="mono" />
        <EmptyNote title={t('release.notFound', { release: name, days: WINDOW_DAYS })}>
          <Link className="btn" to="/o/$orgId/p/$projectId/releases" params={params} search={scope}>
            {t('release.all')}
          </Link>
        </EmptyNote>
      </main>
    )

  const releaseLink = (release: string) => ({
    to: '/o/$orgId/p/$projectId/releases/$release' as const,
    params: { ...params, release },
    search: scope,
  })
  const signal = (key: 'cfs' | 'cfu') =>
    SIGNALS.find((s) => s.key === key) as (typeof SIGNALS)[number]
  const delta = (key: 'cfs' | 'cfu', base: number | null, value: number | null): ReactNode =>
    prev ? (
      <>
        <ChangeCell s={signal(key)} base={base} candidate={value} />
        {` ${t('release.vs', { release: versionBuild(prev.release).version })}`}
      </>
    ) : (
      t('release.firstInWindow')
    )
  const strip: StripItem[] = [
    {
      dt: t('releases.signal.cfs'),
      dd: i18n.number(r.crash_free_sessions_rate * 100, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      small: '%',
      d: delta('cfs', prev?.crash_free_sessions_rate ?? null, r.crash_free_sessions_rate),
    },
    {
      dt: t('releases.signal.cfu'),
      dd:
        r.crash_free_users_rate === null
          ? '—'
          : i18n.number(r.crash_free_users_rate * 100, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
      small: r.crash_free_users_rate === null ? undefined : '%',
      d:
        r.users === null
          ? t('releases.noInstallIds')
          : delta('cfu', prev?.crash_free_users_rate ?? null, r.crash_free_users_rate),
    },
    {
      dt: t('releases.sessions'),
      dd: compact(i18n, r.sessions),
      d:
        r.users === null
          ? t('releases.sinceShipped', { date: date(r.released_at) })
          : t('release.users', { count: r.users, users: compact(i18n, r.users) }),
    },
    {
      dt: t('release.crashed'),
      dd: i18n.number(r.crashed_sessions),
      d:
        r.crashed_users === null
          ? t('releases.crashedSessions')
          : t('release.inUsers', { count: r.crashed_users, users: i18n.number(r.crashed_users) }),
    },
    {
      dt: t('releases.abnormalExits'),
      dd: i18n.number(r.abnormal_exit_sessions),
      d: t('release.noCrashReport'),
    },
  ]
  const cli = prev
    ? cliCompare(prev.release, r.release, appId)
    : `apsio releases${appId ? ` --app ${appId}` : ''}`

  return (
    <main id="main" className="pg rs-pg">
      <PageHead
        crumb={crumb}
        title={r.release}
        variant="mono"
        meta={
          <>
            {t('releases.shipped', { date: date(r.released_at) })}
            {' · '}
            {t('release.share', { share: percentSpaced(i18n, share.get(r.release) ?? 0, 0) })}
            <SymbolsMeta projectId={projectId} release={r.release} appId={appId} />
            {notApplied ? (
              <>
                {' · '}
                {notApplied}
              </>
            ) : null}
          </>
        }
        actions={
          <>
            <StepLink
              to={newer ? releaseLink(newer.release) : undefined}
              label={t('release.newer')}
              title={newer?.release}
              icon="left"
            />
            <StepLink
              to={prev ? releaseLink(prev.release) : undefined}
              label={t('release.older')}
              title={prev?.release}
              icon="right"
            />
            {prev ? (
              <Link
                className="btn"
                to="/o/$orgId/p/$projectId/releases"
                params={params}
                search={{ ...scope, cand: r.release, base: prev.release }}
              >
                {t('release.compareWith', { release: prev.release })}
              </Link>
            ) : null}
            <Link
              className="btn"
              to="/o/$orgId/p/$projectId/issues"
              params={params}
              search={{ ...scope, release: r.release }}
            >
              {t('release.issuesIn', { version: versionBuild(r.release).version })}
            </Link>
            <button
              type="button"
              className="btn"
              onClick={async () =>
                show((await copyText(cli)) ? t('releases.cli.copied') : t('d.code.copyFailed'))
              }
            >
              <Icon name="copy" className="se-i" />
              {t('releases.cli.copy')}
            </button>
          </>
        }
      />
      {sampled ? (
        <p className="notice" role="status">
          {t('releases.estimate', { rate: sampled })}
        </p>
      ) : null}
      <Strip items={strip} label={t('release.strip', { release: r.release })} />

      <div className="grid2">
        <div className="stack">
          <DailySince r={r} prev={prev} appId={appId} projectId={projectId} now={now} />
          <WhatChanged
            r={r}
            prev={prev}
            appId={appId}
            projectId={projectId}
            newIssues={cmp.newIssues}
            regressed={cmp.regressedHere}
            resolved={cmp.resolvedHere}
            pending={cmp.pending}
          />
          <Section
            title={t('release.perf.title')}
            meta={
              prev ? (
                <span translate="no">{`${prev.release} → ${r.release} · ${t('releases.compare.meta', { days: WINDOW_DAYS })}`}</span>
              ) : (
                t('release.noPrevious')
              )
            }
            right={
              <Link
                className="btn ghost sm"
                to="/o/$orgId/p/$projectId/performance"
                params={params}
                search={{ ...scope, release: r.release }}
              >
                {t('nav.performance')}
              </Link>
            }
          >
            {!prev ? (
              <p className="note">{t('releases.lede.first')}</p>
            ) : cmp.pending ? (
              <Loading />
            ) : (
              <PerfVsPrevious
                r={r}
                prev={prev}
                rows={cmp.rows.filter((x) => x.signal.group === 'performance')}
                appId={appId}
                projectId={projectId}
                android={android}
              />
            )}
            <Screens r={r} appId={appId} projectId={projectId} />
          </Section>
          <MissingSymbols r={r} appId={appId} projectId={projectId} app={app} />
        </div>
        <div className="stack">
          <AdoptionByDay r={r} appId={appId} projectId={projectId} now={now} />
          <Breakdown r={r} appId={appId} projectId={projectId} />
          <ReleaseFacts r={r} app={app} appId={appId} projectId={projectId} />
        </div>
      </div>
      {toast}
    </main>
  )
}

/** ‹ and ›: the newer and the older release, or a dimmed button at either end. */
function StepLink({
  to,
  label,
  title,
  icon,
}: {
  to:
    | {
        to: '/o/$orgId/p/$projectId/releases/$release'
        params: { orgId: string; projectId: string; release: string }
        search: { app?: string }
      }
    | undefined
  label: string
  title: string | undefined
  icon: 'left' | 'right'
}) {
  if (!to)
    return (
      <button type="button" className="btn" disabled aria-label={label}>
        <Icon name={icon} />
      </button>
    )
  return (
    <Link className="btn" {...to} aria-label={label} title={title}>
      <Icon name={icon} />
    </Link>
  )
}

function SymbolsMeta({
  projectId,
  release,
  appId,
}: {
  projectId: string
  release: string
  appId: string | undefined
}) {
  const { t } = useI18n()
  const q = useQuery(missingSymbolsQuery(projectId, release, appId))
  if (!q.data || q.data.images === 0) return null
  return (
    <>
      {' · '}
      {q.data.missing.length === 0 ? (
        <Status s="res" label={t('release.symbolsReady')} />
      ) : (
        <Status s="miss" label={t('release.symbolsMissing', { count: q.data.missing.length })} />
      )}
    </>
  )
}

/** The days since `r` shipped, from the window's reads (shared with the list). */
function useShipDays(
  projectId: string,
  appId: string | undefined,
  release: string | undefined,
  now: number,
) {
  const { from, to } = releaseWindow(now)
  return useQuery({
    ...releaseDaysQuery(projectId, appId ?? '', release, from, to),
    enabled: !!appId,
  })
}

const dayOf = (iso: string) => iso.slice(0, 10)

/** Crash-free sessions per day since the release shipped; the release before's first days dashed. */
function DailySince({
  r,
  prev,
  appId,
  projectId,
  now,
}: {
  r: ReleaseHealth
  prev: ReleaseHealth | undefined
  appId: string | undefined
  projectId: string
  now: number
}) {
  const i18n = useI18n()
  const { t, date } = i18n
  const navigate = useNavigate()
  const { orgId } = useProjectParams()
  const mine = useShipDays(projectId, appId, r.release, now)
  const before = useShipDays(projectId, appId, prev?.release, now)
  const limit = useCrashFreeLimit(appId)
  const days = (mine.data ?? []).filter((d) => dayOf(d.day) >= dayOf(r.released_at))
  // The release before only when it shipped in the window: its own first days, day by day.
  const prevDays =
    prev && before.data?.some((d) => dayOf(d.day) === dayOf(prev.released_at))
      ? before.data.filter((d) => dayOf(d.day) >= dayOf(prev.released_at)).slice(0, days.length)
      : []
  const pct = (v: number) =>
    i18n.number(v * 100, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const lastPrev = prevDays.findLast((d) => d.crash_free_sessions_rate !== null)
  return (
    <Section title={t('release.daily.title')} meta={t('release.daily.meta')}>
      {mine.isPending ? (
        <Loading />
      ) : mine.isError ? (
        <ErrorState error={mine.error} retry={() => mine.refetch()} />
      ) : days.every((d) => d.crash_free_sessions_rate === null) ? (
        <p className="note">{t('releases.chart.empty')}</p>
      ) : (
        <LineChart
          label={t('release.daily.label', { release: r.release })}
          points={days.map((d) => ({
            label: date(`${dayOf(d.day)}T00:00:00Z`, {
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC',
            }),
            value: d.crash_free_sessions_rate,
          }))}
          format={pct}
          accent
          timeAxis
          xName={t('chart.day')}
          // A line opens its release's issues (APS-577).
          onPick={(_, s) => {
            const release = s === 0 ? r.release : prev?.release
            if (release)
              navigate({
                to: '/o/$orgId/p/$projectId/issues',
                params: { orgId, projectId },
                search: { ...(appId ? { app: appId } : {}), release },
              })
          }}
          pickHint={() => t('release.daily.open')}
          name={r.release}
          more={
            prev && prevDays.length > 0
              ? [
                  {
                    name: t('release.daily.prev', { release: prev.release, days: prevDays.length }),
                    values: prevDays.map((d) => d.crash_free_sessions_rate),
                    dash: true,
                    end:
                      lastPrev?.crash_free_sessions_rate != null
                        ? `${versionBuild(prev.release).version} ${pct(lastPrev.crash_free_sessions_rate)}`
                        : undefined,
                  },
                ]
              : undefined
          }
          threshold={
            limit !== undefined
              ? { value: limit, label: t('release.daily.alert', { limit: pct(limit) }) }
              : undefined
          }
        />
      )}
    </Section>
  )
}

/** The release's issues grouped New, Regressed, Still open from earlier releases and Resolved. */
function WhatChanged({
  r,
  prev,
  appId,
  projectId,
  newIssues,
  regressed,
  resolved,
  pending,
}: {
  r: ReleaseHealth
  prev: ReleaseHealth | undefined
  appId: string | undefined
  projectId: string
  newIssues: IssueSummary[]
  regressed: IssueSummary[]
  resolved: IssueSummary[]
  pending: boolean
}) {
  const { t } = useI18n()
  const { orgId } = useProjectParams()
  const params = { orgId, projectId }
  const scope = appId ? { app: appId } : {}
  const here = useQuery(
    issuesQuery(projectId, {
      app_id: appId,
      release: r.release,
      days: WINDOW_DAYS,
      sort: 'users',
      state: 'all',
      limit: 50,
    }),
  )
  const inRelease = here.data?.issues ?? []
  // The first release in the window: everything it has is new.
  const fresh = prev ? newIssues : inRelease.filter((i) => i.state?.status !== 'resolved')
  const freshIds = new Set(fresh.map((i) => i.id))
  const regressedIds = new Set(regressed.map((i) => i.id))
  const still = inRelease.filter(
    (i) =>
      !freshIds.has(i.id) &&
      !regressedIds.has(i.id) &&
      (i.state?.status ?? 'unresolved') === 'unresolved',
  )
  const groups: {
    key: string
    title: string
    s: StatusKey
    rows: IssueSummary[]
    counts: boolean
  }[] = [
    {
      key: 'new',
      title: t('release.changed.new', { release: r.release, count: fresh.length }),
      s: 'new',
      rows: fresh,
      counts: true,
    },
    {
      key: 'reg',
      title: t('release.changed.regressed', { count: regressed.length }),
      s: 'reg',
      rows: regressed,
      counts: true,
    },
    {
      key: 'on',
      title: t('release.changed.still', { count: still.length }),
      s: 'on',
      rows: still,
      counts: true,
    },
    {
      key: 'res',
      title: t('release.changed.resolved', { count: resolved.length }),
      s: 'res',
      rows: resolved,
      counts: false,
    },
  ]
  return (
    <Section
      title={t('release.changed.title')}
      meta={
        prev ? (
          <span translate="no">{t('release.changed.against', { release: prev.release })}</span>
        ) : (
          t('release.noPrevious')
        )
      }
      right={
        <Link
          className="btn ghost sm"
          to="/o/$orgId/p/$projectId/issues"
          params={params}
          search={{ ...scope, release: r.release }}
        >
          {t('releases.allIssues')}
        </Link>
      }
    >
      {here.isPending || pending ? (
        <Loading />
      ) : here.isError ? (
        <ErrorState error={here.error} retry={() => here.refetch()} />
      ) : (
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be focusable
        <section className="scroll-x" aria-label={t('release.changed.table')} tabIndex={0}>
          <table className="t">
            <thead>
              <tr>
                <th className="f" scope="col">
                  {t('release.col.state')}
                </th>
                <th scope="col">{t('release.col.kind')}</th>
                <th scope="col">{t('release.col.issue')}</th>
                <th className="num" scope="col">
                  {t('releases.col.sessions')}
                </th>
                <th className="num" scope="col">
                  {t('releases.users')}
                </th>
                <th className="last" scope="col">
                  {t('issues.col.lastSeen')}
                </th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <GroupRows
                  key={g.key}
                  group={g}
                  release={r.release}
                  appId={appId}
                  projectId={projectId}
                />
              ))}
            </tbody>
          </table>
        </section>
      )}
    </Section>
  )
}

function GroupRows({
  group,
  release,
  appId,
  projectId,
}: {
  group: { key: string; title: string; s: StatusKey; rows: IssueSummary[]; counts: boolean }
  release: string
  appId: string | undefined
  projectId: string
}) {
  const i18n = useI18n()
  const { t, relative } = i18n
  const { orgId } = useProjectParams()
  const navigate = useNavigate()
  const params = { orgId, projectId }
  const scope = appId ? { app: appId } : {}
  const rest = group.rows.length - GROUP_ROWS
  return (
    <>
      <tr className="grp">
        <td className="f" colSpan={6}>
          {group.title}
        </td>
      </tr>
      {group.rows.length === 0 ? (
        <tr>
          <td className="f faint" colSpan={6}>
            {t('release.changed.none')}
          </td>
        </tr>
      ) : null}
      {group.rows.slice(0, GROUP_ROWS).map((i) => (
        <tr
          key={i.id}
          className="hov"
          onClick={(e) => {
            if (!(e.target as HTMLElement).closest('a, button'))
              navigate({
                to: '/o/$orgId/p/$projectId/issues/$issueId',
                params: { ...params, issueId: i.id },
                search: scope,
              })
          }}
        >
          <td className="f">
            <Status s={group.s} />
          </td>
          <td>
            <Kind k={i.kind} />
          </td>
          <td className="wrap rs-iss">
            <Link
              className="ttl"
              to="/o/$orgId/p/$projectId/issues/$issueId"
              params={{ ...params, issueId: i.id }}
              search={scope}
              translate="no"
            >
              {issueTitle(i)}
            </Link>
            <span className="t2" translate="no">
              {i.type && i.message ? `${i.id} · ${i.message}` : i.id}
            </span>
          </td>
          <td className="num">
            {group.counts ? i18n.number(i.sessions) : <span className="faint">—</span>}
          </td>
          <td className="num">
            {group.counts ? i18n.number(i.users) : <span className="faint">—</span>}
          </td>
          <td className="last">{relative(i.last_seen)}</td>
        </tr>
      ))}
      {rest > 0 ? (
        <tr>
          <td className="f" colSpan={6}>
            <Link
              className="ul"
              to="/o/$orgId/p/$projectId/issues"
              params={params}
              search={{ ...scope, release }}
            >
              {t('release.changed.more', { count: rest })}
            </Link>
          </td>
        </tr>
      ) : null}
    </>
  )
}

/** Performance against the release before, with where each number comes from. */
function PerfVsPrevious({
  r,
  prev,
  rows,
  appId,
  projectId,
  android,
}: {
  r: ReleaseHealth
  prev: ReleaseHealth
  rows: ReturnType<typeof useComparison>['rows']
  appId: string | undefined
  projectId: string
  android: boolean
}) {
  const i18n = useI18n()
  const { t } = i18n
  const v = useQuery({ ...vitalsQuery(projectId, appId ?? '', r.release), enabled: !!appId })
  const where = (key: string, why: ReactNode): ReactNode => {
    const vit = v.data
    if (!vit) return why
    const count = (n: number | undefined, id: 'release.where.cold' | 'release.where.warm') =>
      n ? <span className="faint">{t(id, { count: compact(i18n, n) })}</span> : null
    if (key === 'cold90')
      return (
        <>
          {count(vit.app_start.cold?.count, 'release.where.cold')}
          {vit.app_start.cold?.count ? ' · ' : null}
          {why}
        </>
      )
    if (key === 'warm90') return count(vit.app_start.warm?.count, 'release.where.warm') ?? why
    if (key === 'freezePerK') {
      const n = android ? vit.hangs.sessions_with_anr : vit.hangs.sessions_with_hang
      return (
        <>
          {why}{' '}
          <span className="faint">
            {t('release.where.sessions', {
              count: i18n.number(n),
              sessions: compact(i18n, vit.hangs.sessions),
            })}
          </span>
        </>
      )
    }
    return why
  }
  return (
    <CompareTable
      label={t('release.perf.table')}
      base={prev.release}
      candidate={r.release}
      rows={rows.map((x) => ({ ...x, why: where(x.signal.key, x.why) }))}
      last={t('release.col.where')}
      groups={false}
      accent={undefined}
    />
  )
}

/** The release's screens, most loaded first: loads, initial and full display, hangs, slow frames. */
function Screens({
  r,
  appId,
  projectId,
}: {
  r: ReleaseHealth
  appId: string | undefined
  projectId: string
}) {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId } = useProjectParams()
  const navigate = useNavigate()
  const scope = { app_id: appId, release: r.release, days: WINDOW_DAYS }
  const screens = useQuery({
    ...screensQuery(projectId, scope, 'loads', SCREENS),
    enabled: !!appId,
  })
  const frames = useQuery({ ...framesQuery(projectId, scope, 'minutes', 50), enabled: !!appId })
  const slow = new Map((frames.data?.screens ?? []).map((s) => [s.screen, s.slow_per_minute]))
  const rows = screens.data ?? []
  if (screens.isPending || rows.length === 0) return null
  const link = (screen: string) => ({
    to: '/o/$orgId/p/$projectId/performance/screens/detail' as const,
    params: { orgId, projectId },
    search: { ...(appId ? { app: appId } : {}), release: r.release, screen },
  })
  return (
    <>
      <div className="sec-h minor">
        <h3>{t('release.screens.title')}</h3>
        <span className="m">{t('release.screens.meta')}</span>
      </div>
      {/* biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be focusable */}
      <section className="scroll-x" aria-label={t('release.screens.title')} tabIndex={0}>
        <table className="t">
          <thead>
            <tr>
              <th className="f" scope="col">
                {t('release.screens.screen')}
              </th>
              <th className="num" scope="col">
                {t('release.screens.loads')}
              </th>
              <th className="num" scope="col" translate="no">
                TTID p50
              </th>
              <th className="num" scope="col" translate="no">
                TTID p90
              </th>
              <th className="num" scope="col" translate="no">
                TTFD p90
              </th>
              <th className="num" scope="col">
                {t('release.screens.hangs')}
              </th>
              <th className="num last" scope="col" title={t('release.screens.slowTitle')}>
                {t('release.screens.slow')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => {
              const sl = slow.get(s.screen)
              return (
                <tr
                  key={s.screen}
                  className="hov"
                  onClick={(e) => {
                    if (!(e.target as HTMLElement).closest('a')) navigate(link(s.screen))
                  }}
                >
                  <td className="f">
                    <Link {...link(s.screen)} translate="no">
                      {s.screen || t('release.screens.none')}
                    </Link>
                  </td>
                  <td className="num">{compact(i18n, s.loads)}</td>
                  <td className="num">
                    <LatencyText v={ttidAt(s, 'p50_ms')} />
                  </td>
                  <td className="num">
                    <LatencyText v={ttidAt(s, 'p90_ms')} />
                  </td>
                  <td className="num">{s.ttfd ? duration(i18n, s.ttfd.p90_ms) : '—'}</td>
                  <td className="num">
                    {s.loads > 0
                      ? i18n.number((s.hangs / s.loads) * 1000, { maximumFractionDigits: 1 })
                      : '—'}
                  </td>
                  <td className="num last">
                    {sl === undefined || sl === null
                      ? '—'
                      : i18n.number(sl, { maximumFractionDigits: 1 })}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </section>
    </>
  )
}

/** The images of the release still without symbols, what each needs and the issues it leaves raw. */
function MissingSymbols({
  r,
  appId,
  projectId,
  app,
}: {
  r: ReleaseHealth
  appId: string | undefined
  projectId: string
  app: App | undefined
}) {
  const i18n = useI18n()
  const { t, rich } = i18n
  const { orgId } = useProjectParams()
  const q = useQuery(missingSymbolsQuery(projectId, r.release, appId))
  const build = versionBuild(r.release).build
  const rows = q.data?.missing ?? []
  // The files the missing images need, each the docs' tab; CodeTabs puts its prose above the
  // window, which keeps commands and config only (APS-307, APS-380).
  const needed = new Set<string>(rows.map((m) => neededFile(m.kind, app?.platform)))
  const all = app ? uploadTabs(i18n, new Set([app.platform])) : []
  const shown = all.filter((x) => needed.has(x.id))
  const tabs = shown.length > 0 ? shown : all
  return (
    <Section
      title={t('release.symbols.title')}
      meta={
        q.data
          ? rows.length > 0
            ? t('release.symbols.metaMissing', { missing: rows.length, images: q.data.images }) +
              (build ? ` · ${t('release.symbols.build', { build })}` : '')
            : t('release.symbols.metaAll', { images: q.data.images })
          : undefined
      }
      right={
        <Link
          className="btn ghost sm"
          to="/o/$orgId/p/$projectId/settings/$section"
          params={{ orgId, projectId, section: 'symbols' }}
        >
          {t('release.symbols.settings')}
        </Link>
      }
    >
      <div id="symbols">
        {q.isPending ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState error={q.error} retry={() => q.refetch()} />
        ) : q.data.images === 0 ? (
          <p className="note">
            {t('releases.symbols.none')} {t('releases.symbols.noneBody')}
          </p>
        ) : rows.length === 0 ? (
          <p className="note">
            <Status s="res" label={t('releases.symbols.all', { images: q.data.images })} />
          </p>
        ) : (
          <>
            {/* biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be focusable */}
            <section className="scroll-x" aria-label={t('release.symbols.table')} tabIndex={0}>
              <table className="t">
                <thead>
                  <tr>
                    <th className="f" scope="col">
                      {t('symbols.col.image')}
                    </th>
                    <th scope="col">{t('release.symbols.arch')}</th>
                    <th scope="col">{t('symbols.col.debugId')}</th>
                    <th scope="col">{t('issues.col.lastSeen')}</th>
                    <th scope="col">{t('release.symbols.affects')}</th>
                    <th className="last" scope="col">
                      {t('symbols.col.needs')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => (
                    <MissingRow
                      key={`${m.app_id}-${m.debug_id}`}
                      m={m}
                      app={app}
                      projectId={projectId}
                      appId={appId}
                    />
                  ))}
                </tbody>
              </table>
            </section>
            <div className="rs-notes">
              {tabs.length > 0 ? (
                <CodeTabs label={t('releases.symbols.upload')} tabs={tabs} notesAbove />
              ) : null}
              <p>
                {rich('release.symbols.note')}{' '}
                <a className="ul" href={SYMBOLS_DOCS} target="_blank" rel="noreferrer">
                  {t('issue.symbols.howTo')}
                </a>
              </p>
            </div>
          </>
        )}
      </div>
    </Section>
  )
}

/** One image without symbols: what it is, what it needs and the issues it leaves raw. */
function MissingRow({
  m,
  app: a,
  projectId,
  appId: id,
}: {
  m: MissingSymbol
  app: App | undefined
  projectId: string
  appId: string | undefined
}) {
  const { t, relative } = useI18n()
  const { orgId } = useProjectParams()
  return (
    <tr>
      <td className="f">
        {m.image_name ? (
          <span className="ttl" translate="no">
            {m.image_name}
          </span>
        ) : (
          <span className="ttl dim">{t('symbols.unnamed')}</span>
        )}
        <span className="t2">{t(KIND_KEY[shownKind(m.kind, a?.platform)])}</span>
      </td>
      <td translate="no">{m.arch}</td>
      <td>
        <code title={m.debug_id} translate="no">
          {shortDebugId(m.debug_id)}
        </code>
      </td>
      <td>{relative(m.last_seen)}</td>
      <td className="aff">
        <AffectedIssues
          orgId={orgId}
          projectId={projectId}
          issues={m.issues}
          count={m.issue_count}
          appId={id}
        />
      </td>
      <td className="last wrap rs-needs">
        <Status s="miss" label={t(NEED_KEY[neededFile(m.kind, a?.platform)])} />
        <span className="t2">{t(SOURCE_KEY[fileSource(m.kind, a?.platform)])}</span>
      </td>
    </tr>
  )
}

const niceMax = (v: number) =>
  [0.01, 0.02, 0.05, 0.1, 0.2, 0.4, 0.6, 0.8, 1].find((s) => s >= v * 1.1) ?? 1

/** The release's share of the app's sessions per day since it shipped. */
function AdoptionByDay({
  r,
  appId,
  projectId,
  now,
}: {
  r: ReleaseHealth
  appId: string | undefined
  projectId: string
  now: number
}) {
  const i18n = useI18n()
  const { t, date } = i18n
  const mine = useShipDays(projectId, appId, r.release, now)
  const app = useShipDays(projectId, appId, undefined, now)
  const appDays = (app.data ?? []).filter((d) => dayOf(d.day) >= dayOf(r.released_at))
  const values = adoptionByDay(mine.data ?? [], appDays)
  const n = values.length
  const label = (i: number) =>
    appDays[i]
      ? date(`${dayOf(appDays[i].day)}T00:00:00Z`, {
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        })
      : ''
  const ticks: [number, string][] =
    n > 2
      ? [
          [0, label(0)],
          [Math.floor((n - 1) / 2), label(Math.floor((n - 1) / 2))],
          [n - 1, label(n - 1)],
        ]
      : n > 0
        ? [[0, label(0)]]
        : []
  const fmt = (v: number) =>
    `${i18n.number(v * 100, { maximumFractionDigits: v * 100 < 2 && v > 0 ? 1 : 0 })} %`
  return (
    <Section title={t('release.adoption.title')} meta={t('release.adoption.meta')}>
      {mine.isPending || app.isPending ? (
        <Loading />
      ) : n === 0 || values.every((v) => v === 0) ? (
        <p className="note">{t('releases.adoption.empty')}</p>
      ) : (
        <Bars
          values={values}
          label={t('release.adoption.label', {
            release: r.release,
            share: percentSpaced(i18n, values[n - 1] ?? 0, 0),
          })}
          height={170}
          max={niceMax(Math.max(...values))}
          format={fmt}
          xLabels={ticks}
          labels={values.map((_, i) => label(i))}
          timeAxis
          xName={t('chart.day')}
          // A few days are a few narrow bars, not one block across the panel.
          gapShare={n < 5 ? 0.6 : 0.3}
          hiFrom={n - 1}
        />
      )}
    </Section>
  )
}

/** Devices and OS versions of the release's sessions (APS-302). */
function Breakdown({
  r,
  appId,
  projectId,
}: {
  r: ReleaseHealth
  appId: string | undefined
  projectId: string
}) {
  const i18n = useI18n()
  const { t } = i18n
  const q = useQuery({ ...breakdownQuery(projectId, r.release, appId ?? ''), enabled: !!appId })
  if (!q.data || q.data.sessions === 0) return null
  const total = q.data.sessions
  const row = (key: string, name: ReactNode, sessions: number) => ({
    key,
    name,
    n: compact(i18n, sessions),
    share: sessions / total,
    value: percentSpaced(i18n, sessions / total, 0),
  })
  const devices = q.data.devices.top.map((d) =>
    row(
      d.device_model,
      <span translate="no">{d.device_model || t('release.unknown')}</span>,
      d.sessions,
    ),
  )
  if (q.data.devices.other_sessions > 0)
    devices.push(row('other', t('release.other'), q.data.devices.other_sessions))
  const oses = q.data.os_versions.top.map((o) =>
    row(
      `${o.os_name} ${o.os_version}`,
      <span translate="no">{`${o.os_name} ${o.os_version}`.trim() || t('release.unknown')}</span>,
      o.sessions,
    ),
  )
  if (q.data.os_versions.other_sessions > 0)
    oses.push(row('other', t('release.other'), q.data.os_versions.other_sessions))
  return (
    <>
      <Section
        title={t('release.devices.title')}
        meta={t('release.devices.meta', { sessions: compact(i18n, total) })}
      >
        <ShareBars label={t('release.devices.title')} rows={devices} />
      </Section>
      <Section title={t('release.os.title')} meta={t('release.os.meta')}>
        <ShareBars label={t('release.os.title')} rows={oses} />
      </Section>
    </>
  )
}

/** The release's facts: version, build, bundle, build ids, shipped, users, last session. */
function ReleaseFacts({
  r,
  app,
  appId,
  projectId,
}: {
  r: ReleaseHealth
  app: App | undefined
  appId: string | undefined
  projectId: string
}) {
  const i18n = useI18n()
  const { t, date, relative } = i18n
  const q = useQuery({ ...breakdownQuery(projectId, r.release, appId ?? ''), enabled: !!appId })
  const { version, build } = versionBuild(r.release)
  const builds = q.data?.builds ?? []
  const items: [ReactNode, ReactNode][] = [
    [
      t('release.kv.version'),
      <span key="v" translate="no">
        {version}
      </span>,
    ],
  ]
  if (build)
    items.push([
      t('release.kv.build'),
      <span key="b" translate="no">
        {build}
      </span>,
    ])
  if (app?.bundle_id)
    items.push([
      t('release.kv.bundle'),
      <span key="bu" translate="no">
        {app.bundle_id}
      </span>,
    ])
  if (builds.length === 1 && builds[0]?.build_id)
    items.push([
      t('release.kv.buildId'),
      <code key="id" title={builds[0].build_id} translate="no">
        {shortDebugId(builds[0].build_id)}
      </code>,
    ])
  else if (builds.length > 1)
    items.push([
      t('release.kv.buildIds'),
      <span key="ids" title={builds.map((b) => b.build_id).join('\n')}>
        {t('release.kv.builds', { count: builds.length })}
      </span>,
    ])
  items.push([t('release.kv.shipped'), date(r.released_at, { dateStyle: 'medium' })])
  items.push([
    t('releases.users'),
    r.users === null
      ? t('releases.noInstallIds')
      : `${i18n.number(r.users)} · ${t('releases.usersNote')}`,
  ])
  items.push([
    t('releases.crashedUsers'),
    r.crashed_users === null ? '—' : i18n.number(r.crashed_users),
  ])
  items.push([t('releases.lastSession'), relative(r.last_seen)])
  return (
    <Section title={t('release.kv.title')} minor>
      <KV items={items} label={t('release.kv.title')} />
    </Section>
  )
}
